import { Component, OnInit, ViewChild, inject } from "@angular/core";
import { CerrixTreeItem } from "@models/tree/CerrixTreeItem";
import { IdNameCombination } from "@models/generic/IdNameCombination";
import { ThirdPartyContractModel } from "@models/thirdparty/ThirdPartyContractModel";
import { ThirdPartyDataService } from "../services/ThirdPartyDataService";
import { FormGroup, FormControl, ReactiveFormsModule, Validators, FormArray } from "@angular/forms";
import { NgSelectModule } from "@ng-select/ng-select";
import { ThirdPartyPermissions } from "@models/thirdparty/ThirdPartyPermissions";
import { TabModel } from "@models/generic/TabModels/TabModel";
import { FormValidationHelper } from "@app/shared/helpers/form-validation-helper";
import { TranslatePipe, injectTranslation, provideTranslationScope } from "@app/shared/i18n/translation-scope";
import { scope } from "@app/thirdparty/i18n/scope";
import { scope as rootScope } from "@app/shared/i18n/scope";
import { ToastrService } from "ngx-toastr";
import { finalize } from "rxjs/operators";
import { TargetModule } from "@enums/document/TargetModule";
import { StandingdataDataService } from "@app/standingdata/shared/standingdata.service";
import { StandingDataType } from "@enums/StandingDataType";
import { TabService } from "@services/tabs/TabService";
import { Pages } from "@constants/pages/Pages";
import { Observable, firstValueFrom } from "rxjs";
import { ThirdPartyContractDataService } from "../services/ThirdPartyContractDataService";
import { TabMenuItem } from "@models/generic/TabModels/TabMenuItem";
import { HistoryOverviewComponent } from "@app/shared/history-overview/history-overview.component";
import { CerrixPromptService } from "@app/shared/services/cerrix-prompt.service";
import { CustomFieldInfoModel } from "@models/customfields/CustomFieldInfoModel";
import { CustomFieldStandingDataModel } from "@models/customfields/CustomFieldStandingDataModel";
import { getCustomFieldValueProperty } from "@methods/CustomFieldMethods";
import { nameof } from "@methods/jeffs-toolkit";
import { ThirdPartyTermOfNoticePeriodEnum } from "@models/thirdparty/ThirdPartyTermOfNoticePeriodEnum";
import { ThirdPartyTermOfNoticePeriodModel } from "@models/thirdparty/ThirdPartyTermOfNoticePeriodModel";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { DestroyRef } from "@angular/core";
import { CerrixTextfieldComponent } from "@app/shared/components/cerrix-textinput/cerrix-textfield/cerrix-textfield.component";
import { CerrixSelectTreeComponent } from "@app/shared/cerrix-select-tree/cerrix-select-tree.component";
import { CerrixSelectComponent } from "@app/shared/cerrix-select/cerrix-select.component";
import { DateInputComponent } from "@app/shared/components/date-input/date-input.component";
import { CheckboxComponent } from "@app/shared/cerrix-checkbox/cerrix-checkbox.component";
import { DocumentManagerOldComponent } from "@app/shared/document-manager-old/document-manager-old.component";
import { CerrixCustomFieldsEditorComponent } from "@app/shared/components/cerrix-custom-fields-editor/cerrix-custom-fields-editor.component";
import { MenuSystemDirective } from "@app/shared/directives/menu-system.directive";
import { DetailOptionsComponent } from "@app/shared/components/detail-options/detail-options.component";
import { DateTimeFormatPipe } from "@app/shared/pipes/date-time-format.pipe";
import { DateFormatPipe } from "@app/shared/pipes/date-format.pipe";

@Component({
    selector: "thirdparty-contracts",
    templateUrl: "./thirdparty-contracts.component.html",
    styleUrls: ["./thirdparty-contracts.component.scss"],
    imports: [
        ReactiveFormsModule,
        NgSelectModule,
        CerrixTextfieldComponent,
        CerrixSelectTreeComponent,
        CerrixSelectComponent,
        DateInputComponent,
        CheckboxComponent,
        DocumentManagerOldComponent,
        CerrixCustomFieldsEditorComponent,
        MenuSystemDirective,
        HistoryOverviewComponent,
        DetailOptionsComponent,
        DateTimeFormatPipe,
        DateFormatPipe,
        TranslatePipe,
    ],
    viewProviders: [provideTranslationScope(scope)],
})

// TODO Make ReviewFrequencyModel more generic
// TODO Pass users, services and frequencies from details to this screen instead of making calls again
export class ThirdPartyContractsComponent implements OnInit {
    private _thirdPartyDS = inject(ThirdPartyDataService);
    private _standingDataDS = inject(StandingdataDataService);
    _contractDS = inject(ThirdPartyContractDataService);
    private _toastr = inject(ToastrService);
    private _tabService = inject(TabService);
    private _pages = inject(Pages);
    private _promptService = inject(CerrixPromptService);
    private _destroyedRef = inject(DestroyRef);
    private _translation = injectTranslation(scope);
    private _rootTranslation = injectTranslation(rootScope);

    @ViewChild(HistoryOverviewComponent) historyOverviewComponent;

    parentForm: FormGroup;
    tab: TabModel;
    services: CerrixTreeItem[];
    termOfNoticeQuantity: number;
    termOfNoticePeriods: ThirdPartyTermOfNoticePeriodModel[];
    contractOwnerRequest: Observable<IdNameCombination[]>;
    contract: ThirdPartyContractModel;
    thirdPartyGuid: string;
    permissions: ThirdPartyPermissions;
    status: any[];

    customFieldData: { [key: number]: CustomFieldInfoModel } = {};
    contractCustomFieldsFormGroup: FormGroup;
    customFieldStandingDataModel: CustomFieldStandingDataModel[] = [];

    sd: any;

    isLoading = true;
    documentTypeTargets = TargetModule.ThirdParty;

    async ngOnInit() {
        // The toasts and term of notice labels below are translated before the template renders.
        await Promise.all([this._translation.ready(), this._rootTranslation.ready()]);

        this.sd = await firstValueFrom(this._thirdPartyDS.getStandingData());

        if (!this.sd) {
            this._toastr.error(this._translation.t("contract.somethingWentWrong"));
            this.tab.close();
        }

        if (this.tab.config) {
            this.thirdPartyGuid = this.tab.config.thirdPartyGuid;
        }

        // These are Third Party permissions, not Contract permissions!
        this.permissions = await firstValueFrom(this._contractDS.getPermissions(this.tab.id));

        let contract: ThirdPartyContractModel;
        if (this.tab.id) {
            contract = await firstValueFrom(this._contractDS.getContractData(this.tab.id));
        } else {
            contract = new ThirdPartyContractModel();
            contract.ThirdPartyGuid = this.thirdPartyGuid;

            // Since these are TP permissions, and the TP is already created, I have to overwrite these for a new contract
            this.permissions.documentPermissions.canAddDocument = false;
            this.permissions.documentPermissions.canAddFolder = false;
        }

        this.initContract(contract);

        const periodLabels: Record<string, string> = {
            None: this._translation.t("contract.periodNone"),
            Day: this._translation.t("contract.periodDay"),
            Week: this._translation.t("contract.periodWeek"),
            Month: this._translation.t("contract.periodMonth"),
        };

        this.termOfNoticePeriods = Object.entries(ThirdPartyTermOfNoticePeriodEnum)
            .filter(([, value]) => typeof value === "number")
            .map(([key, value]) => ({
                label: periodLabels[key] ?? key,
                value: value as number,
            }));
    }

    async initContract(contract: ThirdPartyContractModel) {
        this.contract = contract;

        this.isLoading = false;
        this.tab.showLoader = false;

        this.contractOwnerRequest = this._thirdPartyDS.getUsers(contract.ContractOwner);

        this._thirdPartyDS.getServices().subscribe((value) => this.getServices(value));
        this._standingDataDS
            .getAllByType(StandingDataType.TpContractStatus)
            .subscribe((value) => this.getStatus(value));

        this.parentForm = new FormGroup({
            ContractNumber: new FormControl(contract.ContractNumber, [Validators.required]),
            ContractedValue: new FormControl(contract.ContractedValue, Validators.min(0)),
            Status: new FormControl(contract.Status),
            ContractOwner: new FormControl(contract.ContractOwner),
            SignatureDate: new FormControl(contract.SignatureDate),
            StartDate: new FormControl(contract.StartDate),
            EndDate: new FormControl(contract.EndDate),
            AnnualValue: new FormControl(contract.AnnualValue, Validators.min(0)),
            Framework: new FormControl(contract.Framework),
            Services: new FormControl(contract.Services),
            currencyCode: new FormControl(contract.currencyCode),
            TermOfNoticeQuantity: new FormControl(contract.TermOfNoticeQuantity, [
                Validators.min(1),
                Validators.required,
            ]),
            TermOfNoticePeriod: new FormControl(contract.TermOfNoticePeriod),
        });

        const periodControl = this.parentForm.get("TermOfNoticePeriod");
        const numberControl = this.parentForm.get("TermOfNoticeQuantity");

        if (!periodControl || !numberControl) return;

        const syncDisabledState = (periodValue: number | null) => {
            if (periodValue === ThirdPartyTermOfNoticePeriodEnum.None || periodValue == null) {
                numberControl.setValue(null, { emitEvent: false });
                numberControl.disable({ emitEvent: false });
            } else {
                numberControl.enable({ emitEvent: false });
            }
        };

        syncDisabledState(periodControl.value);

        periodControl.valueChanges
            .pipe(takeUntilDestroyed(this._destroyedRef))
            .subscribe(syncDisabledState);

        await this.createCustomFieldsFormGroup();

        if (!this.permissions.canEdit) {
            this.parentForm.disable();
        }
    }

    async createCustomFieldsFormGroup() {
        const customFieldsFormArray = new FormArray([]);

        this.customFieldStandingDataModel = await firstValueFrom(this._contractDS.getCustomFields());

        if (!this.contract.CustomFields) {
            this.contract.CustomFields = [];
        }

        for (const field of this.customFieldStandingDataModel) {
            const answerField = this.contract.CustomFields.find(
                (x) => x.customFieldId === field.ID,
            );

            const customFieldFormGroup = new FormGroup<any>({
                ID: new FormControl(answerField ? answerField.ID : 0),
                Name: new FormControl(field.Name),
                customFieldId: new FormControl(field.ID),
                required: new FormControl(field.required),
            });

            const valueProperty = getCustomFieldValueProperty(field);
            customFieldFormGroup.addControl(
                valueProperty,
                new FormControl(
                    answerField ? answerField[valueProperty] : null,
                    field.required ? [Validators.required] : [],
                ),
            );

            // Store extra information from the custom field in a custom object which is easier accessible in the front-end
            this.customFieldData[field.ID] = {
                Name: field.Name,
                fieldType: field.fieldType,
                required: field.required,
                answerOptions: field.answerOptions,
            };

            customFieldsFormArray.push(customFieldFormGroup);
        }

        this.contractCustomFieldsFormGroup = new FormGroup({
            customFields: customFieldsFormArray,
        });

        if (this.permissions.canEdit) {
            this.contractCustomFieldsFormGroup.valueChanges.subscribe(() => {
                this.contract.CustomFields =
                    this.contractCustomFieldsFormGroup.value[
                        nameof<ThirdPartyContractModel>((c) => c.CustomFields)
                    ];
            });
        } else {
            this.contractCustomFieldsFormGroup.disable();
        }

        this.parentForm.addControl(
            nameof<ThirdPartyContractModel>((c) => c.CustomFields),
            customFieldsFormArray,
        );
    }

    async save() {
        await Promise.all([this._translation.ready(), this._rootTranslation.ready()]);

        if (this.permissions.canEdit && !this.parentForm.valid) {
            FormValidationHelper.markAllAsTouched(this.parentForm);

            const validationErrors = FormValidationHelper.getFormControlErrors(this.parentForm);
            const validationMessage = FormValidationHelper.getGeneralErrorMessage(
                validationErrors,
                (key, params) => this._rootTranslation.t(key, params),
            );
            this._toastr.warning(validationMessage, this._translation.t("contract.saveFailed"), {
                enableHtml: true,
            });
            return;
        }

        FormValidationHelper.mapToModel(this.parentForm, this.contract);

        const savingPrompt = this._promptService.loader(
            this._translation.t("contract.savingChanges"),
        );
        const call = firstValueFrom(
            this._contractDS
                .storeContract(this.contract)
                .pipe(finalize(() => savingPrompt.close())),
        ).then((v: string) => (this.tab.id = v));

        await call;

        this._toastr.success(this._translation.t("contract.updateCompleted"));
        this.tab.refresh();
    }

    getServices(items: CerrixTreeItem[]) {
        this.services = items;
    }

    getStatus(items: IdNameCombination[]) {
        this.status = items;
    }

    delete() {
        this._contractDS.deleteContract(this.contract.guid).subscribe(
            (validations) => {
                if (validations && validations.length > 0) {
                    this._toastr.warning(validations.toString());
                } else {
                    this._toastr.success(this._translation.t("contract.contractDeleted"));
                    this._tabService.refresh(this._pages.ThirdpartyContractDetail);
                    this.tab.close(false);
                }
            },
            (error) => {
                this._toastr.error("", error || error.Message);
            },
        );
    }

    handleMenuItemClick(menuItem: TabMenuItem) {
        if (menuItem) {
            switch (menuItem.menuItemId) {
                case "history": {
                    this.historyOverviewComponent.loadHistory();
                    break;
                }
            }
        }
    }
}
